<template>
	<!-- Skip Link for Keyboard Navigation — teleported before v-main for first-focus order -->
	<Teleport to="#skip-links-portal">
		<a href="#main-content" class="skip-link" @click.prevent="() => { const el = document.getElementById('main-content'); if (el) el.focus(); }">Skip to main content</a>
	</Teleport>
	<div class="bg-color-granite pb-10" role="main" aria-label="About RISE page content">
		<div class="d-flex justify-center align-center w-100 position-relative" role="banner">
			<v-img class="w-100 h-100"			
				:height="199"
				aspect-ratio="16/9"
				src="https://dellpartnerincentives.com/siteassets/Images/HomePartner/Banner/bgRISE2.jpg"
				alt="RISE Incentive Program banner background"
			></v-img>
			<div class="position-absolute page-header">
				<h1><LanguagePlaceholder default-text="About RISE" /></h1>
			</div>			
		</div>
        <div id="main-content" class="bg-color-granite w-100 h-100">
            <div class="bg-color-quartz w-100 h-100 my-2">
                <v-row no-gutters class="d-flex justify-center w-100 pa-0 pt-3 bg-color-dell-blue tx-color-white" role="region" aria-label="RISE program introduction">
                <v-col cols="10" class="d-flex flex-column justify-start w-100 pa-0 py-8 tx-font-normal tx-font-size20">  
                    <v-row no-gutters>
                        <LanguagePlaceholder default-text='The RISE Incentive Program rewards you for purchasing eligible Dell Technologies products with competitive $-per-box rewards, multipliers, and bonuses - giving you the chance to earn up to $15,000 in rewards per incentive period.' />
                    </v-row>
                    <v-row no-gutters class="pt-10 tx-font-normal tx-font-size18">
                        <v-icon icon="mdi-play-circle-outline" size="x-large" class="pb-2 tx-font-normal" aria-label="Play video icon"></v-icon>
                        <LanguagePlaceholder @custom-event="triggerCustomEvent"
                         is-html 
                         default-text="<custom-event action='showRISEVideo' class='dell-white' aria-label='Watch the RISE overview video on Vimeo'>WATCH</custom-event>: the RISE overview video.">
                        </LanguagePlaceholder>
                    </v-row>

                </v-col>
                </v-row>
            </div>
            <section class="bg-color-granite w-auto h-100 my-2 mx-10" role="region" aria-label="How to earn rewards with RISE">
                <v-row no-gutters class="d-flex justify-center w-100 pa-0">
                <v-col cols="1" class="bg-color-quartz"></v-col>
                <v-col cols="10" class="d-flex justify-start w-100 pa-0 py-10 tx-font-bold bg-color-quartz">  
                    <h2>
                        <LanguagePlaceholder default-text='How to earn rewards with RISE'></LanguagePlaceholder>
                    </h2>   
                </v-col>
                <v-col cols="1" class="bg-color-quartz"></v-col>
                </v-row>
                <v-row no-gutters class="d-flex justify-center w-100 pa-0">
                <v-col cols="1" class="bg-color-quartz"></v-col>
                <v-col cols="10" class="bg-color-white d-flex justify-center w-100 pa-0 tx-font-bold">  
                    <div class="bg-color-white tx-font-bolder ma-0 pa-0">
                        <v-row>
                            <v-col cols="10" class="pa-10">
                                <strong>
                                <LanguagePlaceholder default-text='Core rewards'></LanguagePlaceholder>
                                </strong>
                                <p class="tx-font-bold pb-5">
                                <LanguagePlaceholder default-text='These rewards are earned and logged automatically the moment you purchase a RISE eligible product from a Dell Technologies Authorized Distributor—no reporting needed, we take care of the details for you!'></LanguagePlaceholder>
                                </p>
                                <strong>
                                <LanguagePlaceholder default-text='Multipliers'></LanguagePlaceholder>
                                </strong>
                                <p class="tx-font-bold pb-5">
                                <LanguagePlaceholder default-text='Core rewards can be multiplied by up to 1.6x when specific unit thresholds are reached.'></LanguagePlaceholder>
                                </p>
                                <strong>
                                <LanguagePlaceholder default-text='Bonuses'></LanguagePlaceholder>
                                </strong>
                                <p class="tx-font-bold pb-3">
                                <LanguagePlaceholder 
                                    is-html 
                                    default-text="Discover additional earning opportunities with RISE bonuses. Visit the <route route-name='bonus-dashboard-partner' style='text-decoration: underline;'>Bonus Dashboard</route> to see which bonuses you've qualified for.">
                                </LanguagePlaceholder>
                                </p>
                            </v-col>
                            <v-col cols="2" class="ma-0 pa-0 mt-3 d-flex justify-center align-center bg-color-mist" style="overflow: hidden;">
                                    <v-img
                                        :width="180"
                                        :height="180"
                                        src="https://dellpartnerincentives.com/siteassets/Images/AboutPartner/1.6X.png"
                                        alt="1.6X rewards multiplier illustration"
                                    ></v-img>
                            </v-col>
                        </v-row>                        
                    </div>            
                </v-col>
                <v-col cols="1" class="bg-color-quartz"></v-col>
                </v-row>
                <v-row no-gutters class="d-flex justify-center w-100 pa-0">
                <v-col cols="1" class="bg-color-quartz"></v-col>
                <v-col cols="10" class="d-flex justify-start w-100 pa-0 py-10 tx-font-bold bg-color-quartz">  
                    <h2>
                        <LanguagePlaceholder default-text='How to multiply your rewards'></LanguagePlaceholder>
                    </h2>   
                </v-col>
                <v-col cols="1" class="bg-color-quartz"></v-col>
                </v-row>
                <v-row no-gutters class="d-flex justify-center w-100 pa-0">
                <v-col cols="1" class="bg-color-quartz"></v-col>
                <v-col cols="10" class="d-flex justify-center w-100 pa-0 tx-font-bold">  
                    <div class="bg-color-white tx-font-bolder pa-10 pb-12" role="region" aria-label="Rewards multiplier thresholds by product category">
                        <p class="tx-font-bold pb-10">
                        <LanguagePlaceholder default-text='When you achieve specific thresholds, your rewards will be multiplied by up to 1.6x. Thresholds are independently calculated for each product category. For example, you can achieve your PC+ threshold and receive up to 1.6x your PC+ rewards without needing to meet the unit thresholds for Server+ or Storage+.'></LanguagePlaceholder>
                        </p>
                        <v-row aria-label="PCs, Displays and Peripherals multiplier thresholds">
                        <v-col cols="1" class="bg-color-glacier d-flex justify-center align-center">
                            <v-img
                            :width="50"
                            src="https://dellpartnerincentives.com/siteassets/Images/AboutPartner/iconPeripherals.png"
                            alt=""
                            ></v-img>
                        </v-col>
                        <v-col cols="4" style="border-left: 1px solid #C8C9C7;" class="d-flex justify-center align-start flex-column bg-color-sky tx-color-navy">
                            <h3 class="tx-font-bolder tx-font-size18">
                                <LanguagePlaceholder default-text='PCS+'></LanguagePlaceholder>
                            </h3>
                            <p class="tx-font-normal tx-font-size14 ma-0">
                                <LanguagePlaceholder default-text="PCs units bought as SnS"></LanguagePlaceholder>
                            </p>
                        </v-col>
                        <v-col cols="7">
                            <v-row>
                            <v-col role="cell" aria-label="30 to 79 units threshold">
                                <v-row class="tx-color-white bg-color-steel justify-center py-1 tx-font-normal tx-font-size12 tx-font-bold">
                                <LanguagePlaceholder default-text='30 - 79 Units'></LanguagePlaceholder>
                                </v-row>
                                <v-row class="tx-color-dell-blue bg-color-quartz justify-center tx-font-size32">
                                <LanguagePlaceholder is-html default-text='1.2x'></LanguagePlaceholder>
                                </v-row> 
                                <v-row class="tx-color-dell-blue bg-color-quartz justify-center pb-1 tx-font-normal tx-font-size14 tx-font-bolder">
                                <LanguagePlaceholder is-html default-text='REWARDS'></LanguagePlaceholder>
                                </v-row>    
                            </v-col>
                            <v-col style="border-left: 1px solid #C8C9C7;" role="cell" aria-label="80 to 159 units threshold">
                                <v-row class="tx-color-white bg-color-steel justify-center py-1 tx-font-normal tx-font-size12 tx-font-bold">
                                <LanguagePlaceholder default-text='80 - 159 Units'></LanguagePlaceholder>
                                </v-row>
                                <v-row class="tx-color-cobalt bg-color-quartz justify-center tx-font-size32">
                                <LanguagePlaceholder is-html default-text='1.3x'></LanguagePlaceholder>
                                </v-row> 
                                <v-row class="tx-color-cobalt bg-color-quartz justify-center pb-1 tx-font-normal tx-font-size14 tx-font-bolder">
                                <LanguagePlaceholder is-html default-text='REWARDS'></LanguagePlaceholder>
                                </v-row>    
                            </v-col>
                            <v-col style="border-left: 1px solid #C8C9C7;" role="cell" aria-label="160 to 249 units threshold">
                                <v-row class="tx-color-white bg-color-steel justify-center py-1 tx-font-normal tx-font-size12 tx-font-bold">
                                <LanguagePlaceholder default-text='160 - 249 Units'></LanguagePlaceholder>
                                </v-row>
                                <v-row class="tx-color-royal bg-color-quartz justify-center tx-font-size32">
                                <LanguagePlaceholder is-html default-text='1.4x'></LanguagePlaceholder>
                                </v-row> 
                                <v-row class="tx-color-royal bg-color-quartz justify-center pb-1 tx-font-normal tx-font-size14 tx-font-bolder">
                                <LanguagePlaceholder is-html default-text='REWARDS'></LanguagePlaceholder>
                                </v-row>    
                            </v-col>
                            <v-col style="border-left: 1px solid #C8C9C7;" role="cell" aria-label="250 to 399 units threshold">
                                <v-row class="tx-color-white bg-color-steel justify-center py-1 tx-font-normal tx-font-size12 tx-font-bold">
                                <LanguagePlaceholder default-text='250 - 399 Units'></LanguagePlaceholder>
                                </v-row>
                                <v-row class="tx-color-navy bg-color-quartz justify-center tx-font-size32">
                                <LanguagePlaceholder is-html default-text='1.5x'></LanguagePlaceholder>
                                </v-row> 
                                <v-row class="tx-color-navy bg-color-quartz justify-center pb-1 tx-font-normal tx-font-size14 tx-font-bolder">
                                <LanguagePlaceholder is-html default-text='REWARDS'></LanguagePlaceholder>
                                </v-row>    
                            </v-col>
                            <v-col style="border-left: 1px solid #C8C9C7;" role="cell" aria-label="400 or more units threshold">
                                <v-row class="tx-color-white bg-color-steel justify-center py-1 tx-font-normal tx-font-size12 tx-font-bold">
                                <LanguagePlaceholder default-text='400+ Units'></LanguagePlaceholder>
                                </v-row>
                                <v-row class="tx-color-midnight bg-color-quartz justify-center tx-font-size32">
                                <LanguagePlaceholder is-html default-text='1.6x'></LanguagePlaceholder>
                                </v-row> 
                                <v-row class="tx-color-midnight bg-color-quartz justify-center pb-1 tx-font-normal tx-font-size14 tx-font-bolder">
                                <LanguagePlaceholder is-html default-text='REWARDS'></LanguagePlaceholder>
                                </v-row>    
                            </v-col>
                            </v-row>
                        </v-col>
                        </v-row>

                        <v-row class="ma-0 pa-0" style="height: 10px;">
                        <v-col cols="1" class="ml-16 mt-n2">
                            <v-img style="height: 20px;" class="ml-5"
                            :width="50"
                            src="https://dellpartnerincentives.com/siteassets/Images/AboutPartner/OR.png"
                            alt=""
                            ></v-img>
                        </v-col>
                        <v-col cols="4"></v-col>
                        <v-col cols="7"></v-col>
                        </v-row>

                        <v-row class="mt-2" aria-label="Server multiplier thresholds">
                        <v-col cols="1" class="bg-color-glacier d-flex justify-center align-center">
                            <v-img
                            :width="50"
                            src="https://dellpartnerincentives.com/siteassets/Images/AboutPartner/iconServer.png"
                            alt=""
                            ></v-img>
                        </v-col>
                        <v-col cols="4" style="border-left: 1px solid #C8C9C7;" class="d-flex justify-center align-start flex-column bg-color-cornflower tx-color-navy">
                            <strong>
                            </strong>
                            <p class="tx-font-normal">
                            </p>
                            <h3 class="tx-font-bolder tx-font-size18">
                                <LanguagePlaceholder default-text='SERVER+'></LanguagePlaceholder>
                            </h3>
                            <p class="tx-font-normal tx-font-size14 ma-0">
                                <LanguagePlaceholder default-text="Server units bought as SnS or B2B Smart Selection Offers"></LanguagePlaceholder>
                            </p>
                        </v-col>
                        <v-col cols="7">
                            <v-row>
                            <v-col>
                                <v-row class="tx-color-white bg-color-steel justify-center py-1 tx-font-normal tx-font-size12 tx-font-bold">
                                <LanguagePlaceholder default-text='5 - 8 Units'></LanguagePlaceholder>
                                </v-row>
                                <v-row class="tx-color-dell-blue bg-color-quartz justify-center tx-font-size32">
                                <LanguagePlaceholder is-html default-text='1.2x'></LanguagePlaceholder>
                                </v-row> 
                                <v-row class="tx-color-dell-blue bg-color-quartz justify-center pb-1 tx-font-normal tx-font-size14 tx-font-bolder">
                                <LanguagePlaceholder is-html default-text='REWARDS'></LanguagePlaceholder>
                                </v-row>    
                            </v-col>
                            <v-col style="border-left: 1px solid #C8C9C7;">
                                <v-row class="tx-color-white bg-color-steel justify-center py-1 tx-font-normal tx-font-size12 tx-font-bold">
                                <LanguagePlaceholder default-text='9 - 12 Units'></LanguagePlaceholder>
                                </v-row>
                                <v-row class="tx-color-cobalt bg-color-quartz justify-center tx-font-size32">
                                <LanguagePlaceholder is-html default-text='1.3x'></LanguagePlaceholder>
                                </v-row> 
                                <v-row class="tx-color-cobalt bg-color-quartz justify-center pb-1 tx-font-normal tx-font-size14 tx-font-bolder">
                                <LanguagePlaceholder is-html default-text='REWARDS'></LanguagePlaceholder>
                                </v-row>    
                            </v-col>
                            <v-col style="border-left: 1px solid #C8C9C7;">
                                <v-row class="tx-color-white bg-color-steel justify-center py-1 tx-font-normal tx-font-size12 tx-font-bold">
                                <LanguagePlaceholder default-text='13 - 16 Units'></LanguagePlaceholder>
                                </v-row>
                                <v-row class="tx-color-royal bg-color-quartz justify-center tx-font-size32">
                                <LanguagePlaceholder is-html default-text='1.4x'></LanguagePlaceholder>
                                </v-row> 
                                <v-row class="tx-color-royal bg-color-quartz justify-center pb-1 tx-font-normal tx-font-size14 tx-font-bolder">
                                <LanguagePlaceholder is-html default-text='REWARDS'></LanguagePlaceholder>
                                </v-row>    
                            </v-col>
                            <v-col style="border-left: 1px solid #C8C9C7;">
                                <v-row class="tx-color-white bg-color-steel justify-center py-1 tx-font-normal tx-font-size12 tx-font-bold">
                                <LanguagePlaceholder default-text='17 - 20 Units'></LanguagePlaceholder>
                                </v-row>
                                <v-row class="tx-color-navy bg-color-quartz justify-center tx-font-size32">
                                <LanguagePlaceholder is-html default-text='1.5x'></LanguagePlaceholder>
                                </v-row> 
                                <v-row class="tx-color-navy bg-color-quartz justify-center pb-1 tx-font-normal tx-font-size14 tx-font-bolder">
                                <LanguagePlaceholder is-html default-text='REWARDS'></LanguagePlaceholder>
                                </v-row>    
                            </v-col>
                            <v-col style="border-left: 1px solid #C8C9C7;">
                                <v-row class="tx-color-white bg-color-steel justify-center py-1 tx-font-normal tx-font-size12 tx-font-bold">
                                <LanguagePlaceholder default-text='21+ Units'></LanguagePlaceholder>
                                </v-row>
                                <v-row class="tx-color-midnight bg-color-quartz justify-center tx-font-size32">
                                <LanguagePlaceholder is-html default-text='1.6x'></LanguagePlaceholder>
                                </v-row> 
                                <v-row class="tx-color-midnight bg-color-quartz justify-center pb-1 tx-font-normal tx-font-size14 tx-font-bolder">
                                <LanguagePlaceholder is-html default-text='REWARDS'></LanguagePlaceholder>
                                </v-row>    
                            </v-col>
                            </v-row>
                        </v-col>
                        </v-row>

                        <v-row class="ma-0 pa-0" style="height: 10px;">
                        <v-col cols="1" class="ml-16 mt-n2">
                            <v-img style="height: 20px;" class="ml-5"
                            :width="50"
                            src="https://dellpartnerincentives.com/siteassets/Images/AboutPartner/OR.png"
                            alt=""
                            ></v-img>
                        </v-col>
                        <v-col cols="4"></v-col>
                        <v-col cols="7"></v-col>
                        </v-row>

                        <v-row class="mt-2" aria-label="Storage multiplier thresholds">
                        <v-col cols="1" class="bg-color-glacier d-flex justify-center align-center">
                            <v-img
                            :width="50"
                            src="https://dellpartnerincentives.com/siteassets/Images/AboutPartner/iconStorage.png"
                            alt=""
                            ></v-img>
                        </v-col>
                        <v-col cols="4" style="border-left: 1px solid #C8C9C7;" class="d-flex justify-center align-start flex-column bg-color-dell-blue tx-color-white">
                            <strong>
                            </strong>
                            <p class="tx-font-normal">
                            </p>
                            <h3 class="tx-font-bolder tx-font-size18">
                                <LanguagePlaceholder default-text='STORAGE+'></LanguagePlaceholder>
                            </h3>
                            <p class="tx-font-normal tx-font-size14 ma-0">
                                <LanguagePlaceholder default-text="Storage B2B Smart Selection Offers"></LanguagePlaceholder>
                            </p>
                        </v-col>
                        <v-col cols="7">
                            <v-row>
                            <v-col>
                                <v-row class="tx-color-white bg-color-steel justify-center py-1 tx-font-normal tx-font-size12 tx-font-bold">
                                <LanguagePlaceholder default-text='2 - 4 Units'></LanguagePlaceholder>
                                </v-row>
                                <v-row class="tx-color-dell-blue bg-color-quartz justify-center tx-font-size32">
                                <LanguagePlaceholder is-html default-text='1.2x'></LanguagePlaceholder>
                                </v-row> 
                                <v-row class="tx-color-dell-blue bg-color-quartz justify-center pb-1 tx-font-normal tx-font-size14 tx-font-bolder">
                                <LanguagePlaceholder is-html default-text='REWARDS'></LanguagePlaceholder>
                                </v-row>    
                            </v-col>
                            <v-col style="border-left: 1px solid #C8C9C7;">
                                <v-row class="tx-color-white bg-color-steel justify-center py-1 tx-font-normal tx-font-size12 tx-font-bold">
                                <LanguagePlaceholder default-text='5 - 7 Units'></LanguagePlaceholder>
                                </v-row>
                                <v-row class="tx-color-cobalt bg-color-quartz justify-center tx-font-size32">
                                <LanguagePlaceholder is-html default-text='1.3x'></LanguagePlaceholder>
                                </v-row> 
                                <v-row class="tx-color-cobalt bg-color-quartz justify-center pb-1 tx-font-normal tx-font-size14 tx-font-bolder">
                                <LanguagePlaceholder is-html default-text='REWARDS'></LanguagePlaceholder>
                                </v-row>    
                            </v-col>
                            <v-col style="border-left: 1px solid #C8C9C7;">
                                <v-row class="tx-color-white bg-color-steel justify-center py-1 tx-font-normal tx-font-size12 tx-font-bold">
                                <LanguagePlaceholder default-text='8 - 10 Units'></LanguagePlaceholder>
                                </v-row>
                                <v-row class="tx-color-royal bg-color-quartz justify-center tx-font-size32">
                                <LanguagePlaceholder is-html default-text='1.4x'></LanguagePlaceholder>
                                </v-row> 
                                <v-row class="tx-color-royal bg-color-quartz justify-center pb-1 tx-font-normal tx-font-size14 tx-font-bolder">
                                <LanguagePlaceholder is-html default-text='REWARDS'></LanguagePlaceholder>
                                </v-row>    
                            </v-col>
                            <v-col style="border-left: 1px solid #C8C9C7;">
                                <v-row class="tx-color-white bg-color-steel justify-center py-1 tx-font-normal tx-font-size12 tx-font-bold">
                                <LanguagePlaceholder default-text='11 - 12 Units'></LanguagePlaceholder>
                                </v-row>
                                <v-row class="tx-color-navy bg-color-quartz justify-center tx-font-size32">
                                <LanguagePlaceholder is-html default-text='1.5x'></LanguagePlaceholder>
                                </v-row> 
                                <v-row class="tx-color-navy bg-color-quartz justify-center pb-1 tx-font-normal tx-font-size14 tx-font-bolder">
                                <LanguagePlaceholder is-html default-text='REWARDS'></LanguagePlaceholder>
                                </v-row>    
                            </v-col>
                            <v-col style="border-left: 1px solid #C8C9C7;">
                                <v-row class="tx-color-white bg-color-steel justify-center py-1 tx-font-normal tx-font-size12 tx-font-bold">
                                <LanguagePlaceholder default-text='13+ Units'></LanguagePlaceholder>
                                </v-row>
                                <v-row class="tx-color-midnight bg-color-quartz justify-center tx-font-size32">
                                <LanguagePlaceholder is-html default-text='1.6x'></LanguagePlaceholder>
                                </v-row> 
                                <v-row class="tx-color-midnight bg-color-quartz justify-center pb-1 tx-font-normal tx-font-size14 tx-font-bolder">
                                <LanguagePlaceholder is-html default-text='REWARDS'></LanguagePlaceholder>
                                </v-row>    
                            </v-col>
                            </v-row>
                        </v-col>
                        </v-row>
                        <p class="tx-font-bold pt-10">
                        <LanguagePlaceholder default-text='Only PC hardware units (laptops, desktops and workstations) count towards your PCs+Displays+Peripherals reward multiplier thresholds (displays and peripherals do not). Once thresholds have been achieved, the rewards multiplier is applied to all products purchased within this category, including accessories.'></LanguagePlaceholder>
                        </p>
                    </div>            
                </v-col>
                <v-col cols="1" class="bg-color-quartz"></v-col>
                </v-row>
                <v-row no-gutters class="d-flex justify-center w-100 pa-0">
                <v-col cols="1" class="bg-color-quartz"></v-col>
                <v-col cols="10" class="d-flex justify-start w-100 pa-0 py-10 tx-font-bold bg-color-quartz">  
                    <h2>
                        <LanguagePlaceholder default-text='How your rewards are paid'></LanguagePlaceholder>
                    </h2>   
                </v-col>
                <v-col cols="1" class="bg-color-quartz"></v-col>
                </v-row>
                <v-row no-gutters class="d-flex justify-center w-100 pa-0">
                <v-col cols="1" class="bg-color-quartz"></v-col>
                <v-col cols="10" class="bg-color-quartz d-flex justify-center w-100 pa-0 pb-10 tx-font-bold">  
                    <div class="bg-color-white tx-font-bolder w-100 h-100 mb-5" role="region" aria-label="How rewards are paid">
                        <v-row>
                        <v-col cols="10" class="pl-8 pt-10 mb-0 pb-0">
                            <strong>
                            <LanguagePlaceholder default-text='At the end of each incentive period, your rewards will be calculated and paid out in e-vouchers*.'></LanguagePlaceholder>
                            </strong>
                            <div class="d-flex my-5">
                            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<p>1.</p>&nbsp;&nbsp;&nbsp;
                            <p class="tx-font-bold pb-2">
                                <strong class="tx-font-bolder">
                                    <LanguagePlaceholder default-text="If you earned between $50-$15,000" />
                                </strong>
                                <br>
                                <LanguagePlaceholder default-text='Rewards will be issued via e-voucher to spend with your Dell Technologies Authorized Distributors on a wide range of Dell Technologies products.'></LanguagePlaceholder>
                            </p>
                            </div>
                            <div class="d-flex">
                            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<p>2.</p>&nbsp;&nbsp;&nbsp;
                            <p class="tx-font-bold pb-2">
                                <strong class="tx-font-bolder">
                                    <LanguagePlaceholder default-text="If you earned under $50" />
                                </strong>
                                <br>
                                <LanguagePlaceholder default-text='Rewards under $50 will roll over to the next incentive period, allowing you to accumulate them over time and put them toward larger purchases.'></LanguagePlaceholder>
                            </p>
                            </div>
                            <div class="d-flex justify-center align-center pa-5 ma-2 bg-color-cornflower tx-color-navy tx-font-normal w-25">
                            <LanguagePlaceholder is-html default-text='<a href="https://dellpartnerincentives.com/files/Acquisitions/Partner%20Presentation/RISE%20Partner%20Introductory%20Guide%20EN.pdf" class="dell-white" aria-label="Download RISE Partner Introductory Guide PDF" target="_blank" rel="noopener noreferrer">Click here</a> for more information.'></LanguagePlaceholder>
                            </div>    
                            <div class="d-flex justify-start align-center pa-3 ma-2 pl-0 tx-color-royal tx-font-normal tx-font-bold">
                            <LanguagePlaceholder is-html default-text='* Please note, certain countries are paid via credit note only for local legislative reasons.'></LanguagePlaceholder>
                            </div>  
                        </v-col>
                        <v-col cols="2" class="ma-0 pa-0">
                            <div class="w-100 h-100 my-2 pt-1" style="overflow: hidden;">
                                <v-img cover class="h-100"
                                :width="220"
                                :height="180"
                                src="https://dellpartnerincentives.com/siteassets/Images/AboutPartner/$15000(Blue).gif"
                                alt="$5000 threshold illustration"
                                ></v-img>
                            </div>
                        </v-col>
                        </v-row>                
                    </div>           
                </v-col>
                <v-col cols="1" class="bg-color-quartz"></v-col>
                </v-row>
            </section>
            <section class="bg-color-cobalt tx-color-white w-100 h-100 my-2" role="region" aria-label="Step by step guide">
                <v-row no-gutters class="d-flex justify-center w-100 pa-0">
                <v-col cols="10" class="d-flex justify-start w-100 pa-0 pt-10 tx-font-bold">  
                    <h2>
                        <LanguagePlaceholder default-text='Step by step guide'></LanguagePlaceholder>
                    </h2>   
                </v-col>
                </v-row>
                <v-row no-gutters class="d-flex justify-center w-100 pa-0 pt-3 pb-10 bg-color-cobalt tx-color-white">
                    <v-col cols="10" class="d-flex flex-column justify-start w-100 pa-0 tx-font-bold">  
                        <v-card>
                            <v-tabs v-model="tab" grow color="#1D65C0" aria-label="Step by step guide navigation" role="tablist">
                                <v-tab class="text-none bg-color-glacier tx-color-navy" :value="1" 
                                       id="tab-1"
                                       aria-label="Step 1: Purchase eligible products" 
                                       role="tab" 
                                       :aria-selected="tab === 1"
                                       tabindex="0">
                                    <LanguagePlaceholder 
                                    default-text="1. Purchase" />
                                </v-tab>
                                <v-tab class="text-none bg-color-mist tx-color-navy" :value="2" 
                                       id="tab-2"
                                       aria-label="Step 2: Earn rewards" 
                                       role="tab" 
                                       :aria-selected="tab === 2"
                                       tabindex="0">
                                    <LanguagePlaceholder 
                                    default-text="2. Earn Rewards" />
                                </v-tab>
                                <v-tab class="text-none bg-color-pool tx-color-navy" :value="3" 
                                       id="tab-3"
                                       aria-label="Step 3: Grow rewards with multipliers" 
                                       role="tab" 
                                       :aria-selected="tab === 3"
                                       tabindex="0">
                                    <LanguagePlaceholder 
                                    default-text="3. Grow Rewards" />
                                    </v-tab>
                                <v-tab class="text-none bg-color-sky tx-color-navy" :value="4" 
                                       id="tab-4"
                                       aria-label="Step 4: Spend rewards" 
                                       role="tab" 
                                       :aria-selected="tab === 4"
                                       tabindex="0">
                                    <LanguagePlaceholder 
                                    default-text="4. Spend Rewards" />
                                    </v-tab>
                            </v-tabs>
                            <v-tabs-window v-model="tab" role="tabpanel" aria-label="Step content">
                                <v-tabs-window-item :value="1" role="tabpanel" aria-labelledby="tab-1" :aria-hidden="tab !== 1">
                                    <v-card flat>
                                        <v-card-text>
                                            <div class="tx-color-navy tx-font-normal pa-5 pb-0">
                                                <strong class="tx-color-navy tx-font-bold">
                                                    <LanguagePlaceholder default-text='Earn $ rewards for purchasing RISE eligible products from participating Authorized Distributors.'></LanguagePlaceholder>
                                                </strong>
                                            </div>
                                        </v-card-text>
                                        <div class="tx-color-white px-10 pb-5 pt-2">
                                            <v-btn class="text-none bg-color-dell-blue" @click="systemStore.routerTo('home-eligible-products')" @keydown.enter="systemStore.routerTo('home-eligible-products')" aria-label="View eligible products page" tabindex="0" role="link">
                                                <LanguagePlaceholder default-text="View Eligible Products" />
                                            </v-btn>  
                                        </div>
                                    </v-card>
                                </v-tabs-window-item>

                                <v-tabs-window-item :value="2" role="tabpanel" aria-labelledby="tab-2" :aria-hidden="tab !== 2">
                                    <v-card flat>
                                        <v-card-text>
                                            <div class="tx-color-navy tx-font-normal pa-5 pb-0">
                                                <strong class="tx-color-navy tx-font-bold">
                                                    <p class="mb-2"><v-icon icon="mdi-circle-small" aria-hidden="true"></v-icon>
                                                        <LanguagePlaceholder default-text="Your $ rewards will accumulate throughout the 13-week incentive period."></LanguagePlaceholder>
                                                    </p>
                                                    <p class="mb-2"><v-icon icon="mdi-circle-small" aria-hidden="true"></v-icon>
                                                        <LanguagePlaceholder default-text="There's no need to report your purchases. Simply order and your purchases are automatically logged."></LanguagePlaceholder>
                                                    </p>
                                                    <p class="mb-2"><v-icon icon="mdi-circle-small" aria-hidden="true"></v-icon>
                                                        <LanguagePlaceholder default-text="Track how much you have earned using your 'My Performance' report."></LanguagePlaceholder>
                                                    </p>
                                                </strong>
                                            </div>
                                        </v-card-text>
                                        <div class="tx-color-white px-10 pb-5">
                                            <v-btn class="text-none bg-color-dell-blue" @click="systemStore.routerTo('my-performance')" @keydown.enter="systemStore.routerTo('my-performance')" aria-label="View my performance report" tabindex="0" role="link">
                                                <LanguagePlaceholder default-text="My Performance" />
                                            </v-btn>  
                                        </div>
                                    </v-card>
                                </v-tabs-window-item>

                                <v-tabs-window-item :value="3" role="tabpanel" aria-labelledby="tab-3" :aria-hidden="tab !== 3">
                                    <v-card flat>
                                        <v-card-text>
                                            <div class="tx-color-navy tx-font-normal pa-5 pb-0">
                                                <strong class="tx-color-navy tx-font-bold">
                                                    <p class="mb-2"><v-icon icon="mdi-circle-small" aria-hidden="true"></v-icon>
                                                        <LanguagePlaceholder default-text="Core rewards can be multiplied by up to 1.6x when specific unit thresholds are reached."></LanguagePlaceholder>
                                                    </p>
                                                    <p class="mb-2"><v-icon icon="mdi-circle-small" aria-hidden="true"></v-icon>
                                                        <LanguagePlaceholder default-text="You can also maximise your earnings with RISE bonuses—updated each incentive period."></LanguagePlaceholder>
                                                    </p>
                                                </strong>
                                            </div>
                                        </v-card-text>
                                        <div class="tx-color-white px-10 pb-5">
                                            <v-btn class="text-none bg-color-dell-blue" @click="systemStore.routerTo('bonus-dashboard-partner')" @keydown.enter="systemStore.routerTo('bonus-dashboard-partner')" aria-label="View bonus dashboard" tabindex="0" role="link">
                                                <LanguagePlaceholder default-text="Bonus Dashboard" />
                                            </v-btn>  
                                        </div>
                                    </v-card>
                                </v-tabs-window-item>

                                <v-tabs-window-item :value="4" role="tabpanel" aria-labelledby="tab-4" :aria-hidden="tab !== 4">
                                    <v-card flat>
                                        <v-card-text>
                                            <div class="tx-color-navy tx-font-normal pa-5 pb-0">
                                                <strong class="tx-color-navy tx-font-bold">
                                                    <LanguagePlaceholder default-text='At the start of the next incentive period, your rewards will be calculated and paid out in the following two ways:'></LanguagePlaceholder>
                                                </strong>
                                                <br><br>
                                                <strong class="tx-color-navy tx-font-bolder">
                                                    <LanguagePlaceholder default-text="If you earned between $50 and $15,000" />
                                                </strong>
                                                <br>
                                                <LanguagePlaceholder default-text="Rewards will be issued via e-vouchers to spend with your Dell Technologies Authorized Distributors on a wide range of Dell Technologies products." />
                                                <br><br>
                                                <strong class="tx-color-navy tx-font-bolder">
                                                    <LanguagePlaceholder default-text="If you earned under $50" />
                                                </strong>
                                                <br>
                                                <LanguagePlaceholder default-text="Rewards under $50 will roll over to the next incentive period, allowing you to accumulate them over time and put them toward larger purchases." />
                                            </div>
                                        </v-card-text>
                                        <div class="tx-color-white px-10 pb-5 pt-2">
                                            <v-btn class="text-none bg-color-dell-blue" @click="systemStore.routerTo('redemption')" @keydown.enter="systemStore.routerTo('redemption')" aria-label="Redeem my rewards" tabindex="0" role="link">
                                                <LanguagePlaceholder default-text="Redeem My Rewards" />
                                            </v-btn>  
                                        </div>
                                    </v-card>
                                </v-tabs-window-item>
                            </v-tabs-window>
                        </v-card>
                    </v-col>
                </v-row>
            </section>     
        </div>

        <v-dialog persistent v-model="showVideo" width="650" role="dialog" aria-labelledby="video-dialog-title">
            <v-card width="650">
                <v-card-title id="video-dialog-title">Welcome To Rise Video</v-card-title>
                <v-card-text>

                    <v-row style="padding:60% 0 0 0;position:relative;">
                        <iframe width="600" height="338" controls preload="none" 
                        :src="vimeoEmbedSrc"
                        frameborder="0" 
                        allow="autoplay; fullscreen; picture-in-picture; clipboard-write" 
                        style="position:absolute;top:0;left:0;width:100%;height:100%;" 
                        title="Welcome To RISE - Introduction video for the RISE Partner Incentive Program"
                        aria-label="Welcome To RISE video player"></iframe>
                    </v-row>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="showVideo = false" aria-label="Close video dialog">
                        <LanguagePlaceholder default-text="Close" />
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

    </div>
</template>

<script setup>
import { ref, computed, onBeforeMount } from 'vue'
import { useSystemStore } from '@/stores/system'
import LanguagePlaceholder from '@/components/LanguagePlaceholder.vue'

const systemStore = useSystemStore()
const showVideo = ref(false);

const VIMEO_PLAYER_QUERY =
    'title=0&byline=0&portrait=0&badge=0&autopause=0&player_id=0&app_id=58479'

// Map ISO language codes (lowercase) to Vimeo video IDs.
// Fallback behavior: if a language isn't mapped, use English.
const vimeoVideoIdByLanguage = Object.freeze({
    en: '1174417709',
    // fr: 'PUT_FRENCH_VIDEO_ID_HERE',
    de: '1174423704',
    es: '1174423791',
    it: '1174423876',
    pl: '1174423961',
    tr: '1174424049',
})

const vimeoEmbedSrc = computed(() => {
    const lang = (systemStore.selectedLanguage || '').toString().toLowerCase()
    const videoId = vimeoVideoIdByLanguage[lang] || vimeoVideoIdByLanguage.en
    return `https://player.vimeo.com/video/${videoId}?${VIMEO_PLAYER_QUERY}`
})

const triggerCustomEvent = (action) => {
    if (action == 'showRISEVideo') {
        showVideo.value = true
    }
}

const tab = ref(null)

</script>

<style scoped>

/* Ensure proper focus indicators */
.v-btn:focus,
.v-tab:focus,
a:focus {
  outline: 2px solid #0078d4;
  outline-offset: 2px;
}

/* Improve color contrast for better accessibility */
.tx-color-white {
  color: #ffffff !important;
}

/* Ensure proper contrast for text on colored backgrounds */
.bg-color-steel .tx-color-white {
  color: #ffffff !important;
}
</style>